import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  Typography
} from '@mui/material';
import { organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { getClusterChipStyle } from '../../../utils/clusterUtils';
import { CLUSTER_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = CLUSTER_RETURN_TYPES;

const ClusterSelector = ({
  multiple = false,
  leafOnly = false,
  value = multiple ? [] : '',
  onChange,
  label = 'Clusters',
  placeholder = 'Select clusters...',
  required = false,
  disabled = false,
  fullWidth = true,
  size = 'medium',
  helperText = '',
  error = false,
  sx = {},
  returnValue = CLUSTER_RETURN_TYPES.ID,
  allowSelectAll = false, // Allow selecting "All Clusters" option
  allOptionLabel = 'All Clusters', // Label for the "all" option
  allOptionValue = '', // Value to use for "all" option
  // ADR-119: render each cluster's Organizational Structure Type (code in the option
  // row, "NAME · CODE" in chips). Default off so existing call sites are
  // unchanged; switched on where the extra context helps.
  showStructureType = false,
  // ADR-119: id of a cluster whose node AND descendants are disabled — used by the
  // cluster form's parent picker so a cluster cannot be placed under itself.
  excludeSubtreeOf = null,
  // ADR-119: offer only clusters whose Organizational Structure Type code is in
  // this list (e.g. [REGION_STRUCTURE_TYPE_CODE] → "Regions"). The list is
  // flat — the cluster hierarchy is not meaningful once ancestors are filtered out.
  structureTypeCodes = null,
  // ADR-119 amendment 1: a stored value may point at a cluster the filter above
  // excludes (set before the filter existed, or through the API). Such a value
  // is still rendered — disabled and suffixed with this text — so the field
  // never shows a raw id and the reason is visible. Null = no suffix.
  outOfScopeSuffix = null,
  // ADR-167 D3a: clusters whose Organizational Structure Type code is in this
  // list are shown but cannot be chosen (the cluster form's parent picker:
  // a Region cannot contain other clusters). Shown with this suffix.
  disabledStructureTypeCodes = null,
  disabledStructureTypeSuffix = '',
  // Remove open, onOpen, onClose props - let Material-UI handle dropdown state
}) => {
  const { showAlert } = useAlert();
  const [clusters, setClusters] = useState([]);
  const [hierarchicalClusters, setHierarchicalClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      setLoading(true);
      const allClusters = await organizationApi.getClusters();
      const codeFilter = Array.isArray(structureTypeCodes) && structureTypeCodes.length > 0
        ? new Set(structureTypeCodes)
        : null;
      const clustersData = codeFilter
        ? allClusters.filter(c => c.orgStructureType?.code && codeFilter.has(c.orgStructureType.code))
        : allClusters;
      
      // Label/name resolution always works off the FULL list, so a value the
      // type filter excludes still renders as a name rather than a raw id.
      setClusters(allClusters);

      if (codeFilter) {
        // Flat, alphabetical: filtered clusters may sit at any depth of the tree
        setHierarchicalClusters(
          [...clustersData]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(c => ({ ...c, level: 0, children: [] }))
        );
        return;
      }
      
      // Build hierarchical structure
      const buildHierarchy = (items, parentId = null, level = 0) => {
        return items
          .filter(item => (!parentId && !item.parentCluster) || (item.parentCluster?.id === parentId))
          .map(item => ({
            ...item,
            level,
            children: buildHierarchy(items, item.id, level + 1)
          }));
      };

      const hierarchical = buildHierarchy(clustersData);
      setHierarchicalClusters(hierarchical);
    } catch (err) {
      showAlert('Failed to load clusters.', 'error', err);
      console.error('Error fetching clusters');
    } finally {
      setLoading(false);
    }
  };

  // ADR-119 amendment 1: ids the current option list actually offers.
  const offeredIds = useMemo(() => {
    const ids = new Set();
    const walk = (nodes) => nodes.forEach(node => {
      ids.add(node.id);
      if (node.children && node.children.length > 0) walk(node.children);
    });
    walk(hierarchicalClusters);
    return ids;
  }, [hierarchicalClusters]);

  const isOutOfScope = (clusterId) =>
    offeredIds.size > 0 && Boolean(clusterId) && !offeredIds.has(clusterId);

  // ADR-119: chip / single-value label, optionally suffixed with the type code
  // and, for a value outside the filtered list, with outOfScopeSuffix.
  const getClusterLabel = (cluster) => {
    if (!cluster) return '';
    const code = showStructureType ? cluster.orgStructureType?.code : null;
    const base = code ? `${cluster.name} · ${code}` : cluster.name;
    return outOfScopeSuffix && isOutOfScope(cluster.id)
      ? `${base} (${outOfScopeSuffix})`
      : base;
  };

  // ADR-119 amendment 1: selected clusters the filtered list does not contain. They
  // are rendered as disabled options so the value stays legible, MUI does not
  // warn about an out-of-range value, and the cluster cannot be re-selected once
  // the user moves away from it.
  const getOutOfScopeClusters = () => {
    if (loading || offeredIds.size === 0) return [];
    const internalValue = getInternalValue();
    const selectedIds = multiple
      ? (Array.isArray(internalValue) ? internalValue : [])
      : (internalValue ? [internalValue] : []);
    return selectedIds
      .filter(id => id && id !== allOptionValue && isOutOfScope(id))
      .map(id => clusters.find(c => c.id === id))
      .filter(Boolean);
  };

  // Helper function to get the display value for a cluster ID
  const getClusterDisplayValue = (clusterId) => {
    const cluster = clusters.find(c => c.id === clusterId);
    return cluster ? cluster.name : clusterId;
  };

  // Helper function to get cluster ID from name.
  // ADR-119 amendment 1: prefer a cluster the current (possibly type-filtered) list
  // offers, so a name carried by both an offered and a filtered-out cluster resolves
  // to the offered one. Name-based call sites (e.g. the CSV import) depend on this.
  const getClusterIdFromName = (clusterName) => {
    const cluster = clusters.find(c => c.name === clusterName && offeredIds.has(c.id))
      || clusters.find(c => c.name === clusterName);
    return cluster ? cluster.id : null;
  };

  // Helper function to convert between return value types
  const convertValue = (inputValue, fromType, toType) => {
    if (fromType === toType) return inputValue;
    
    if (multiple) {
      if (!Array.isArray(inputValue)) return [];
      return inputValue.map(val => {
        if (fromType === CLUSTER_RETURN_TYPES.ID && toType === CLUSTER_RETURN_TYPES.NAME) {
          return getClusterDisplayValue(val);
        } else if (fromType === CLUSTER_RETURN_TYPES.NAME && toType === CLUSTER_RETURN_TYPES.ID) {
          return getClusterIdFromName(val);
        }
        return val;
      }).filter(Boolean);
    } else {
      if (fromType === CLUSTER_RETURN_TYPES.ID && toType === CLUSTER_RETURN_TYPES.NAME) {
        return getClusterDisplayValue(inputValue);
      } else if (fromType === CLUSTER_RETURN_TYPES.NAME && toType === CLUSTER_RETURN_TYPES.ID) {
        return getClusterIdFromName(inputValue);
      }
      return inputValue;
    }
  };

  // Get the internal value (always work with IDs internally)
  const getInternalValue = () => {
    if (returnValue === CLUSTER_RETURN_TYPES.ID) {
      return value;
    } else {
      // Convert names to IDs for internal use
      return convertValue(value, CLUSTER_RETURN_TYPES.NAME, CLUSTER_RETURN_TYPES.ID);
    }
  };

  const renderClusterOptions = (clusters, parentChain = []) => {
    const options = [];
    
    // Get internal value once for this render
    const internalValue = getInternalValue();
    
    clusters.forEach(cluster => {
      // Determine if this is a leaf cluster by checking if it has children
      const isLeaf = !cluster.children || cluster.children.length === 0;
      // ADR-119: the excluded cluster and everything below it cannot be chosen
      const isInExcludedSubtree = Boolean(excludeSubtreeOf)
        && (cluster.id === excludeSubtreeOf || parentChain.includes(excludeSubtreeOf));
      // ADR-167 D3a: e.g. Regions in a parent picker
      const isDisabledType = Array.isArray(disabledStructureTypeCodes)
        && Boolean(cluster.orgStructureType?.code)
        && disabledStructureTypeCodes.includes(cluster.orgStructureType.code);
      const isSelectable = (leafOnly ? isLeaf : true) && !isInExcludedSubtree && !isDisabledType;
      
      // Check if this cluster is currently selected (work with internal IDs)
      // Only consider selected if we have a valid internal value
      const isCurrentlySelected = internalValue && (multiple 
        ? Array.isArray(internalValue) && internalValue.includes(cluster.id)
        : internalValue === cluster.id);
      
      // Add the current cluster option
      options.push(
        <MenuItem 
          key={cluster.id} 
          value={cluster.id}
          disabled={!isSelectable}
          selected={isCurrentlySelected} // Show as selected even if not selectable
          sx={{
            pl: cluster.level ? `${cluster.level * 20 + 16}px` : '16px',
            borderLeft: cluster.level ? `${cluster.level}px solid #f3f4f6` : 'none',
            color: !isSelectable ? '#9CA3AF' : 'inherit',
            opacity: !isSelectable ? 0.9 : 1,
            backgroundColor: isCurrentlySelected && !isSelectable ? '#E3F2FD' : 'inherit',
            '&:hover': {
              backgroundColor: isCurrentlySelected && !isSelectable ? '#BBDEFB' : undefined
            }
          }}
        >
          {cluster.level > 0 && '└ '}
          {cluster.name}
          {isInExcludedSubtree && cluster.id === excludeSubtreeOf && ' (this cluster)'}
          {isDisabledType && !isInExcludedSubtree && disabledStructureTypeSuffix}
          {showStructureType && cluster.orgStructureType?.code && (
            <Typography
              component="span"
              variant="caption"
              title={cluster.orgStructureType.name}
              sx={{ ml: 'auto', pl: 2, color: 'text.secondary', fontFamily: 'monospace' }}
            >
              {cluster.orgStructureType.code}
            </Typography>
          )}
        </MenuItem>
      );
      
      // Add children recursively
      if (cluster.children && cluster.children.length > 0) {
        const childOptions = renderClusterOptions(cluster.children, [...parentChain, cluster.id]);
        childOptions.forEach(childOption => options.push(childOption));
      }
    });
    
    return options;
  };

  const handleSelectionChange = (event) => {
    let selectedValue = event.target.value;
    
    if (multiple && leafOnly) {
      // Filter out any non-leaf clusters that might have been selected
      selectedValue = selectedValue.filter(id => {
        const findClusterInHierarchy = (clusters, targetId) => {
          for (const cluster of clusters) {
            if (cluster.id === targetId) {
              return cluster;
            }
            if (cluster.children && cluster.children.length > 0) {
              const found = findClusterInHierarchy(cluster.children, targetId);
              if (found) return found;
            }
          }
          return null;
        };
        
        const cluster = findClusterInHierarchy(hierarchicalClusters, id);
        // Only allow selection if it's a leaf (no children)
        return cluster && (!cluster.children || cluster.children.length === 0);
      });
    }
    
    // Convert to the desired return type before calling onChange
    const returnedValue = convertValue(selectedValue, CLUSTER_RETURN_TYPES.ID, returnValue);
    if (onChange) {
      onChange(returnedValue);
    }
  };



  const renderNormalValue = () => {
    const internalValue = getInternalValue();
    
    if (multiple) {
      if (!internalValue || internalValue.length === 0) {
        return <em style={{ color: '#9CA3AF' }}>{placeholder}</em>;
      }
      
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {internalValue.map((value) => {
            // Handle "all" option
            if (allowSelectAll && value === allOptionValue) {
              return (
                <Chip 
                  key={value} 
                  label={allOptionLabel} 
                  size="small"
                  sx={getClusterChipStyle()}
                />
              );
            }
            
            const cluster = clusters.find(c => c.id === value);
            return cluster ? (
              <Chip 
                key={value} 
                label={getClusterLabel(cluster)} 
                size="small"
                sx={getClusterChipStyle()}
              />
            ) : null;
          })}
        </Box>
      );
    } else {
      if (!internalValue && internalValue !== allOptionValue) {
        return <em style={{ color: '#9CA3AF' }}>{placeholder}</em>;
      }
      
      // Handle "all" option for single select
      if (allowSelectAll && internalValue === allOptionValue) {
        return allOptionLabel;
      }
      
      const cluster = clusters.find(c => c.id === internalValue);
      return cluster ? getClusterLabel(cluster) : internalValue;
    }
  };

  const renderValue = () => {
    return renderNormalValue();
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      required={required} 
      disabled={disabled || loading} 
      size={size}
      error={error}
      sx={sx}
    >
      <InputLabel>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: '2px solid #e0e0e0',
                borderTop: '2px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            Loading clusters...
          </Box>
        ) : label}
      </InputLabel>
      <Select
        multiple={multiple}
        value={loading ? (multiple ? [] : '') : (getInternalValue() || (multiple ? [] : ''))}
        onChange={handleSelectionChange}
        label={loading ? 'Loading clusters...' : label}
        renderValue={loading ? undefined : renderValue}
        input={multiple ? <OutlinedInput label={loading ? 'Loading clusters...' : label} /> : undefined}

        MenuProps={{
          PaperProps: {
            style: { 
              maxHeight: 300,
              zIndex: 1350 // Ensure dropdown appears above table
            }
          }
        }}
      >
        {hierarchicalClusters.length === 0 ? [
          <MenuItem key="no-data" disabled>
            <Typography variant="body2" color="text.secondary">
              No clusters available
            </Typography>
          </MenuItem>
        ] : [
          // Add "All Clusters" option if allowed and not in multiple mode
          ...(allowSelectAll && !multiple ? [
            <MenuItem key="all-option" value={allOptionValue}>
              <em>{allOptionLabel}</em>
            </MenuItem>
          ] : []),
          ...getOutOfScopeClusters().map(cluster => (
            <MenuItem
              key={cluster.id}
              value={cluster.id}
              disabled
              sx={{ color: '#9CA3AF', opacity: 0.9, backgroundColor: '#E3F2FD' }}
            >
              {cluster.name}
              {outOfScopeSuffix ? ` (${outOfScopeSuffix})` : ''}
            </MenuItem>
          )),
          ...(leafOnly ? [
            <MenuItem key="helper-text" disabled>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', px: 1, py: 0.5 }}>
                Only leaf clusters can be selected
              </Typography>
            </MenuItem>
          ] : []),
          ...renderClusterOptions(hierarchicalClusters)
        ]}
      </Select>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default ClusterSelector; 